/**
 * Compliance Dashboard Page - Redesigned to match user's exact specifications
 * Main Center Compliance Dashboard with:
 * - Inspection Readiness header bar
 * - 4 main cards: Staff Compliance, Child Records, Attendance Status, CACFP Compliance
 * - 3 alert sections: Staff, Child, Center (with Director Notes)
 * - Upcoming Deadlines section (real data from staff/child records)
 * - Inspection Mode section with report buttons
 * - Staff-to-Child Ratio Compliance Calculator
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import AppShell from "@/components/layout/AppShell";
import {
  Users, Baby, ClipboardCheck, AlertTriangle, AlertCircle,
  FileText, Calendar, ChevronRight, CheckCircle, XCircle,
  Utensils, Building, MessageSquare, Folder, Plus, Trash2,
  StickyNote, Upload, Download, Pin, PinOff, Moon, UserCheck,
  Printer, Eye
} from "lucide-react";

// Import components
import InspectionMode from "@/components/compliance/InspectionMode";
import EnrolledChildrenReport from "@/components/reports/EnrolledChildrenReport";
import { ComplianceChecklistManager } from "@/components/compliance";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Settings, ChevronDown, ChevronUp, ArrowDown, ShieldCheck, TrendingUp } from "lucide-react";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import FeatureGate from "@/components/FeatureGate";
import PrintReportHeader from "@/components/common/PrintReportHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// State-mandated ratios
const RATIOS = {
  infant: { label: 'Infants (0-12mo)', ratio: 4, napRatio: 8 },
  youngToddler: { label: 'Young Toddlers (12-24mo)', ratio: 5, napRatio: 10 },
  olderToddler: { label: 'Older Toddlers (24-36mo)', ratio: 6, napRatio: 12 },
  preschool: { label: 'Preschool (3-5yr)', ratio: 10, napRatio: 20 },
  schoolAge: { label: 'School Age (5+)', ratio: 15, napRatio: 30 }
};

// School-Age Ratio Logic Helper
// During school year (Sept-May): Track AM drop-offs, reset at 11 AM for PM arrivals
// During summer (June-Aug): Count all day like full-time
const getSchoolAgeRatioStatus = () => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11 (Jan=0, Dec=11)
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();
  
  // Summer months: June(5), July(6), August(7)
  const isSummer = currentMonth >= 5 && currentMonth <= 7;
  
  // Time windows:
  // Before 8:30 AM: AM drop-offs at center
  // 8:30 AM - 11 AM: AM kids at school (show "at school" count)
  // After 11 AM: Reset, count PM drop-offs only
  const isBefore830AM = currentHour < 8 || (currentHour === 8 && currentMinutes < 30);
  const isBefore11AM = currentHour < 11;
  
  if (isSummer) {
    return {
      isSummer: true,
      isSchoolYear: false,
      countMode: 'summer',
      showAtSchool: false,
      message: 'Summer - School Age counted all day'
    };
  } else {
    // School year (Sept-May)
    if (isBefore830AM) {
      // Before 8:30 AM - AM kids at center
      return {
        isSummer: false,
        isSchoolYear: true,
        countMode: 'am',
        showAtSchool: false,
        message: 'Before 8:30 AM (AM drop-offs at center)'
      };
    } else if (isBefore11AM) {
      // 8:30 AM - 11 AM: AM kids went to school
      return {
        isSummer: false,
        isSchoolYear: true,
        countMode: 'am_at_school',
        showAtSchool: true,
        message: '8:30-11 AM (AM kids at school)'
      };
    } else {
      // After 11 AM - reset, count PM drop-offs only
      return {
        isSummer: false,
        isSchoolYear: true,
        countMode: 'pm',
        showAtSchool: false,
        message: 'After 11 AM (PM drop-offs)'
      };
    }
  }
};

// Determine if a specific school-age child should be counted in ratios
// Based on their actual sign-in status and current time of day/year
const shouldCountSchoolAgeChild = (signin, status) => {
  // Summer: Count like full-time - have dropOffTime and not picked up
  if (status.countMode === 'summer') {
    // For school-age in summer, use the standard AM drop-off as their full-day drop-off
    // OR use regular dropOffTime for consistency with full-time children
    const hasDropOff = signin.amParentDropOffTime || signin.dropOffTime;
    const isPickedUp = signin.pmParentPickupTime || signin.pickUpTime;
    return hasDropOff && !isPickedUp;
  }
  
  // School year, before 8:30 AM: Count if AM drop-off exists
  if (status.countMode === 'am') {
    return !!signin.amParentDropOffTime;
  }
  
  // School year, after 8:30 AM: Only count if PM school drop-off exists (returned from school)
  // and not yet picked up by parent
  if (status.countMode === 'pm') {
    return signin.pmSchoolAgeDropOffTime && !signin.pmParentPickupTime;
  }
  
  return false;
};

const ComplianceDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, organization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [showInspectionMode, setShowInspectionMode] = useState(false);
  const [scrollToBinder, setScrollToBinder] = useState(false);
  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false);
  
  // Data states
  const [readinessScore, setReadinessScore] = useState(null);
  const [complianceStatus, setComplianceStatus] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [inspectionMode, setInspectionMode] = useState(null);
  
  // Dashboard stats from API
  const [staffStats, setStaffStats] = useState({ total: 0, expiring: 0, actionNeeded: 0 });
  const [childStats, setChildStats] = useState({ total: 0, expiring: 0, missing: 0, expired: 0 });
  const [attendanceStats, setAttendanceStats] = useState({ signedIn: 0, staffOnDuty: 0, ratiosOk: true });
  
  // CACFP data - real data from API
  const [cacfpStats, setCacfpStats] = useState({ 
    todayMeals: { breakfast: 0, lunch: 0, snack: 0 },
    monthToDate: { breakfast: 0, lunch: 0, snack: 0 },
    currentDate: '',
    monthName: ''
  });
  
  // Upcoming deadlines from real staff/child expiration dates
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  
  // Team/Center messages
  const [teamMessage, setTeamMessage] = useState(null);
  const [recentMessages, setRecentMessages] = useState([]);
  
  // Director Notes
  const [directorNotes, setDirectorNotes] = useState([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  
  // Director Documents
  const [directorDocs, setDirectorDocs] = useState([]);
  const [docUploadTitle, setDocUploadTitle] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docFileInputRef = useRef(null);
  
  // Enrolled Children Report
  const [showEnrolledReport, setShowEnrolledReport] = useState(false);
  
  // Compliance Checklist Manager
  const [showChecklistManager, setShowChecklistManager] = useState(false);
  const [complianceItems, setComplianceItems] = useState([]);

  // Staff compliance visibility toggle
  const [showComplianceToStaff, setShowComplianceToStaff] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  
  // Staff Ratio Calculator State
  const [staffOnDuty, setStaffOnDuty] = useState(0);
  const [isNapTime, setIsNapTime] = useState(false);
  const [schoolAgeAtSchoolOverride, setSchoolAgeAtSchoolOverride] = useState(null); // null = auto, true = force at school, false = force at center
  const [childrenByAge, setChildrenByAge] = useState({
    infant: 0,
    youngToddler: 0,
    olderToddler: 0,
    preschool: 0,
    schoolAge: 0
  });
  const [ratioCompliance, setRatioCompliance] = useState(null);
  const [schoolAgeStatus, setSchoolAgeStatus] = useState(getSchoolAgeRatioStatus());

  // Expandable missing items state
  const [expandedStaffItems, setExpandedStaffItems] = useState({}); // Track which staff items are expanded
  const [expandedChildItems, setExpandedChildItems] = useState({}); // Track which child items are expanded
  const [showAllStaff, setShowAllStaff] = useState(false); // Show all staff instead of just 4
  const [showAllChildren, setShowAllChildren] = useState(false); // Show all children instead of just 4

  // Update school age status periodically (every minute)
  useEffect(() => {
    const updateSchoolAgeStatus = () => {
      setSchoolAgeStatus(getSchoolAgeRatioStatus());
    };
    
    const interval = setInterval(updateSchoolAgeStatus, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Re-fetch attendance when override changes
  useEffect(() => {
    if (hasAccess) {
      getTodayAttendance();
    }
  }, [schoolAgeAtSchoolOverride]);

  // Role-based permissions matrix (loaded from Owner Tools settings) — same
  // source the sidebar nav already uses to decide who can click into this page.
  const [permissionMatrix, setPermissionMatrix] = useState({});
  useEffect(() => {
    axios.get(`${API}/owner/permissions`)
      .then(r => setPermissionMatrix(r.data?.permissions || {}))
      .catch(() => {});
  }, []);
  const checkMatrix = (featureKey) => {
    if (user?.role === 'owner') return true;
    const rolePerms = permissionMatrix[featureKey];
    if (!rolePerms) return true;
    return rolePerms[user?.role] !== false;
  };

  // Check if user has access (owner or co-owner always; other roles per the permissions matrix)
  const hasAccess = user?.role === 'owner' || user?.role?.toLowerCase() === 'co-owner' || user?.isPrimaryOwner || checkMatrix('compliance_dashboard');
  
  // Calculate ratio compliance using WEIGHTED SLOTS METHOD
  // Each child "uses" a fraction of staff capacity based on their age group ratio
  // Example: 1 infant uses 1/4 staff capacity, 1 preschooler uses 1/10 staff capacity
  // Total staff needed = sum of all weighted slots, rounded up
  const calculateRatioCompliance = useCallback(() => {
    if (staffOnDuty <= 0) {
      setRatioCompliance(null);
      return;
    }
    
    // Get current school-age status for display purposes
    const saStatus = getSchoolAgeRatioStatus();
    
    // childrenByAge already has correct counts from getTodayAttendance
    const totalChildren = Object.values(childrenByAge).reduce((sum, count) => sum + (parseInt(count) || 0), 0);
    
    // Get school-age counts from attendance stats for display
    const schoolAgeAtSchoolCount = attendanceStats.schoolAgeAtSchool || 0;
    const schoolAgeInRatioCount = childrenByAge.schoolAge || 0;
    
    if (totalChildren === 0) {
      setRatioCompliance({ 
        compliant: true, 
        message: 'No children in ratio count', 
        details: [],
        schoolAgeAtSchool: saStatus.schoolAgeAtSchool,
        schoolAgeExcluded: schoolAgeAtSchoolCount,
        schoolAgeInRatio: schoolAgeInRatioCount,
        schoolAgeMessage: saStatus.message,
        countMode: saStatus.countMode
      });
      return;
    }
    
    // WEIGHTED SLOTS METHOD: Calculate fractional staff needs per age group
    // Each child uses (1 / ratio) of a staff member's capacity
    let totalWeightedSlots = 0;
    const details = [];
    let youngestAgeGroup = null;
    const ageOrder = ['infant', 'youngToddler', 'olderToddler', 'preschool', 'schoolAge'];
    
    for (const age of ageOrder) {
      const numCount = parseInt(childrenByAge[age]) || 0;
      if (numCount > 0) {
        // Track youngest age group for display
        if (!youngestAgeGroup) {
          youngestAgeGroup = age;
        }
        
        const ratio = isNapTime ? RATIOS[age].napRatio : RATIOS[age].ratio;
        // Weighted slots: each child uses (1/ratio) of staff capacity
        const weightedSlots = numCount / ratio;
        totalWeightedSlots += weightedSlots;
        
        // For display, show the fractional staff contribution
        details.push({
          ageGroup: RATIOS[age].label,
          children: numCount,
          ratio: `1:${ratio}`,
          weightedSlots: weightedSlots.toFixed(2),
          staffContribution: `${weightedSlots.toFixed(2)} staff capacity`
        });
      }
    }
    
    // Add excluded school-age info if there are school-age children at school
    if (schoolAgeAtSchoolCount > 0) {
      details.push({
        ageGroup: 'School Age (At School)',
        children: schoolAgeAtSchoolCount,
        ratio: 'N/A',
        weightedSlots: '0.00',
        staffContribution: 'Not counted',
        excluded: true
      });
    }
    
    // Round up the total weighted slots to get required staff
    const requiredStaff = Math.ceil(totalWeightedSlots);
    const compliant = staffOnDuty >= requiredStaff;
    
    // Determine if this is a mixed age group scenario
    const activeAgeGroups = ageOrder.filter(age => (parseInt(childrenByAge[age]) || 0) > 0);
    const isMixedAgeGroup = activeAgeGroups.length > 1;
    
    setRatioCompliance({
      compliant,
      totalChildren,
      staffOnDuty: parseInt(staffOnDuty),
      requiredStaff,
      totalWeightedSlots: totalWeightedSlots.toFixed(2),
      isMixedAgeGroup,
      activeAgeGroups: activeAgeGroups.length,
      youngestAgeGroup: youngestAgeGroup,
      youngestAgeLabel: RATIOS[youngestAgeGroup]?.label,
      isNapTime,
      schoolAgeAtSchool: saStatus.schoolAgeAtSchool,
      schoolAgeExcluded: schoolAgeAtSchoolCount,
      schoolAgeInRatio: schoolAgeInRatioCount,
      schoolAgeMessage: saStatus.message,
      isSummer: saStatus.isSummer,
      countMode: saStatus.countMode,
      message: compliant 
        ? `✓ Compliant - ${staffOnDuty} staff for ${totalChildren} children (weighted need: ${totalWeightedSlots.toFixed(1)} → ${requiredStaff})`
        : `✗ Non-Compliant - Need ${requiredStaff} staff (weighted: ${totalWeightedSlots.toFixed(1)}), have ${staffOnDuty}`,
      details
    });
  }, [staffOnDuty, childrenByAge, isNapTime, attendanceStats.schoolAgeAtSchool]);

  // Fetch compliance data
  const fetchComplianceData = useCallback(async () => {
    try {
      const [scoreRes, statusRes, alertsRes, modeRes, dashboardRes, notesRes, docsRes, itemsRes] = await Promise.all([
        axios.get(`${API}/compliance/readiness-score`),
        axios.get(`${API}/compliance/status`),
        axios.get(`${API}/compliance/alerts`),
        axios.get(`${API}/compliance/inspection-mode`),
        axios.get(`${API}/compliance/dashboard-stats`),
        axios.get(`${API}/compliance/director-notes`),
        axios.get(`${API}/compliance/director-documents`),
        axios.get(`${API}/compliance/items`)
      ]);

      setReadinessScore(scoreRes.data);
      setComplianceStatus(statusRes.data.items || []);
      setComplianceItems(statusRes.data.items || []); // Use status data which includes paper copy info
      setAlerts(alertsRes.data.alerts || []);
      setInspectionMode(modeRes.data);
      
      // Dashboard stats
      const stats = dashboardRes.data;
      setStaffStats(stats.staffStats || { total: 0, expiring: 0, actionNeeded: 0 });
      setChildStats(stats.childStats || { total: 0, expiring: 0, missing: 0 });
      
      // CACFP data
      if (stats.cacfp) {
        setCacfpStats({
          todayMeals: stats.cacfp.today || { breakfast: 0, lunch: 0, snack: 0 },
          monthToDate: stats.cacfp.monthToDate || { breakfast: 0, lunch: 0, snack: 0 },
          currentDate: stats.cacfp.currentDate,
          monthName: stats.cacfp.monthName
        });
      }
      
      // Upcoming deadlines (real data from staff/child records)
      setUpcomingDeadlines(stats.upcomingDeadlines || []);
      
      // Team messages
      setTeamMessage(stats.teamMessage);
      setRecentMessages(stats.recentMessages || []);
      
      // Director notes and documents
      setDirectorNotes(notesRes.data.notes || []);
      setDirectorDocs(docsRes.data.documents || []);

      // Compliance visibility setting
      try {
        const visRes = await axios.get(`${API}/organizations/compliance-visibility`);
        setShowComplianceToStaff(visRes.data?.showComplianceToStaff || false);
      } catch {}
      
    } catch (error) {
      console.error("Error fetching compliance data:", error);
      // Set defaults on error
      setReadinessScore({ score: 0, level: 'critical' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch compliance items with status (used by FacilityDocumentsChecklist)
  const fetchComplianceItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/compliance/status`);
      setComplianceItems(response.data.items || []);
      // Also refresh readiness score
      const scoreRes = await axios.get(`${API}/compliance/readiness-score`);
      setReadinessScore(scoreRes.data);
    } catch (error) {
      console.error("Error fetching compliance items:", error);
    }
  }, []);
  
  // Director Notes functions
  const addDirectorNote = async () => {
    if (!newNoteContent.trim()) return;
    
    setSavingNote(true);
    try {
      const response = await axios.post(`${API}/compliance/director-notes`, {
        content: newNoteContent,
        isPinned: false
      });
      setDirectorNotes([response.data, ...directorNotes]);
      setNewNoteContent('');
      setShowAddNote(false);
      toast.success("Note added");
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };
  
  const deleteDirectorNote = async (noteId) => {
    try {
      await axios.delete(`${API}/compliance/director-notes/${noteId}`);
      setDirectorNotes(directorNotes.filter(n => n.id !== noteId));
      toast.success("Note deleted");
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };
  
  const togglePinNote = async (note) => {
    try {
      await axios.put(`${API}/compliance/director-notes/${note.id}`, {
        isPinned: !note.isPinned
      });
      setDirectorNotes(directorNotes.map(n => 
        n.id === note.id ? { ...n, isPinned: !n.isPinned } : n
      ));
    } catch (error) {
      toast.error("Failed to update note");
    }
  };
  
  // Upload document(s)
  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!docUploadTitle.trim()) { toast.error("Please enter a name for the file"); return; }

    setUploadingDoc(true);
    try {
      const uploaded = [];
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        formData.append('title', files.length > 1 ? `${docUploadTitle} (${i + 1})` : docUploadTitle);
        const response = await axios.post(`${API}/compliance/director-documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push(response.data);
      }
      setDirectorDocs([...uploaded.reverse(), ...directorDocs]);
      toast.success(files.length > 1 ? `${files.length} documents uploaded` : "Document uploaded");
      setDocUploadTitle("");
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploadingDoc(false);
      if (docFileInputRef.current) docFileInputRef.current.value = "";
    }
  };
  
  const deleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/compliance/director-documents/${docId}`);
      setDirectorDocs(directorDocs.filter(d => d.id !== docId));
      toast.success("Document deleted");
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  // Fetch a director document's base64 file data and open it as a blob URL
  const openDirectorDocument = async (doc, shouldPrint) => {
    try {
      const res = await axios.get(`${API}/compliance/director-documents/${doc.id}/download`);
      const { fileData, fileType, fileName } = res.data;
      const byteChars = atob(fileData);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: fileType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        toast.error("Please allow pop-ups for this site to view the document");
        return;
      }
      if (shouldPrint) {
        win.onload = () => win.print();
      }
    } catch (error) {
      toast.error(`Failed to open ${doc.title || 'document'}`);
    }
  };

  // Print all Inspection Notes & Reminders together in one dated report
  const printNotesReport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Please allow pop-ups for this site to view the report");
      return;
    }
    const centerName = escapeHtml(organization?.name || 'Childcare Center');
    const rows = (directorNotes || []).map(note =>
      '<div class="note-item' + (note.isPinned ? ' pinned' : '') + '">' +
      '<p class="note-content">' + escapeHtml(note.content) + '</p>' +
      '<p class="note-date">' + new Date(note.createdAt).toLocaleDateString() + (note.isPinned ? ' &middot; Pinned' : '') + '</p>' +
      '</div>'
    ).join('');
    printWindow.document.write(
      '<!DOCTYPE html><html><head>' +
      '<title>Inspection Notes & Reminders - ' + centerName + '</title>' +
      '<style>' +
      'body { font-family: Arial, sans-serif; padding: 30px; max-width: 800px; margin: 0 auto; }' +
      '.header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #1a365d; padding-bottom: 15px; }' +
      '.center-name { font-size: 16px; color: #333; font-weight: bold; }' +
      '.report-title { font-size: 14px; color: #1a365d; font-weight: bold; margin-top: 5px; }' +
      '.date { font-size: 11px; color: #888; margin-top: 5px; }' +
      '.note-item { border: 1px solid #ddd; border-radius: 6px; padding: 10px 12px; margin-bottom: 10px; }' +
      '.note-item.pinned { background: #f5f3ff; border-color: #c4b5fd; }' +
      '.note-content { font-size: 13px; color: #333; margin: 0 0 4px 0; white-space: pre-wrap; }' +
      '.note-date { font-size: 10px; color: #888; margin: 0; }' +
      '.empty { text-align: center; color: #888; font-size: 13px; padding: 20px; }' +
      '@media print { body { padding: 15px; } }' +
      '</style></head><body>' +
      '<div class="header">' +
      '<div class="center-name">' + centerName + '</div>' +
      '<div class="report-title">INSPECTION NOTES &amp; REMINDERS</div>' +
      '<div class="date">Generated: ' + new Date().toLocaleDateString() + ' at ' + new Date().toLocaleTimeString() + '</div>' +
      '</div>' +
      (rows || '<div class="empty">No notes yet.</div>') +
      '</body></html>'
    );
    printWindow.document.close();
    printWindow.print();
  };

  useEffect(() => {
    if (!hasAccess) {
      toast.error("Access denied. Only Owners and Co-Owners can access the Compliance Dashboard.");
      navigate("/");
      return;
    }
    fetchComplianceData();
  }, [hasAccess, fetchComplianceData, navigate]);

  // Handle URL params for inspection mode and scroll to binder
  useEffect(() => {
    const mode = searchParams.get('mode');
    const scrollTarget = searchParams.get('scrollTo');
    
    if (mode === 'inspection') {
      setShowInspectionMode(true);
      // If scrollTo=binder is present, trigger scroll after inspection mode loads
      if (scrollTarget === 'binder') {
        setScrollToBinder(true);
      }
      // Clear the URL params after handling (keeps URL clean)
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleUpdate = () => {
    fetchComplianceData();
  };

  // Get today's attendance and children by age
  // Applies special logic for school-age children based on time of day and season
  const getTodayAttendance = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const [signinsRes, childrenRes] = await Promise.all([
        axios.get(`${API}/signins?date=${today}`),
        axios.get(`${API}/children`)
      ]);
      
      const signins = signinsRes.data || [];
      const children = childrenRes.data.children || childrenRes.data || [];
      
      // Get current school-age status for ratio logic
      let saStatus = getSchoolAgeRatioStatus();
      
      // Apply manual override if set
      if (schoolAgeAtSchoolOverride !== null) {
        if (schoolAgeAtSchoolOverride === true) {
          // Force "at school" mode - don't count school-age in ratio
          saStatus = {
            ...saStatus,
            schoolAgeAtSchool: true,
            countMode: 'override_at_school',
            message: 'Manual Override: At School (not in ratio)'
          };
        } else {
          // Force "at center" mode - count all school-age in ratio
          saStatus = {
            ...saStatus,
            schoolAgeAtSchool: false,
            countMode: 'override_at_center',
            message: 'Manual Override: At Center (counted in ratio)'
          };
        }
      }
      
      // Calculate children by age group
      const ageCounts = {
        infant: 0,
        youngToddler: 0,
        olderToddler: 0,
        preschool: 0,
        schoolAge: 0
      };
      
      // Track total signed-in for display (includes all signed-in regardless of ratio)
      let totalSignedInCount = 0;
      // Track school-age children details for status display
      let schoolAgeTotal = 0;
      let schoolAgeInRatio = 0;
      let schoolAgeAtSchool = 0;
      
      signins.forEach(signin => {
        const child = children.find(c => c.id === signin.childId);
        if (!child) return;
        
        // Use signin category first (more reliable), fallback to child category
        const signinCategory = (signin.category || '').toLowerCase();
        const childCategory = (child.category || '').toLowerCase();
        const category = signinCategory || childCategory;
        
        // Calculate age for ratio grouping
        const birthdate = child.birthdate ? new Date(child.birthdate) : null;
        const todayDate = new Date();
        let ageInMonths = 0;
        
        if (birthdate) {
          ageInMonths = (todayDate.getFullYear() - birthdate.getFullYear()) * 12 + 
                        (todayDate.getMonth() - birthdate.getMonth());
        }
        
        // Determine if this is a school-age child based on category
        const isSchoolAge = category.includes('school') || category === 'school_age';
        
        if (isSchoolAge) {
          // School-age attendance:
          // - Before 8:30 AM: AM drop-offs at center (in ratio)
          // - 8:30 AM - 11 AM: AM kids at school (show "at school" count, not in ratio)
          // - After 11 AM: Reset, count PM drop-offs only
          // - Summer: Count all like full-time
          
          const hasAmDropOff = !!signin.amParentDropOffTime;
          const hasPmDropOff = !!signin.pmSchoolAgeDropOffTime;
          const isPickedUp = !!signin.pmParentPickupTime;
          
          // Skip if already picked up - no longer at center
          if (isPickedUp) {
            return;
          }
          
          // Determine if counted based on override or auto logic
          let countInRatio = false;
          let countAtSchool = false;
          
          if (schoolAgeAtSchoolOverride === true) {
            // Manual override: "At School" - only count PM drop-offs in ratio
            countInRatio = hasPmDropOff;
            countAtSchool = hasAmDropOff && !hasPmDropOff;
          } else if (schoolAgeAtSchoolOverride === false) {
            // Manual override: "At Center" - count all who have any drop-off
            countInRatio = hasAmDropOff || hasPmDropOff;
            countAtSchool = false;
          } else {
            // Auto mode - time-based logic
            if (saStatus.countMode === 'summer') {
              // Summer: Count all like full-time
              countInRatio = (hasAmDropOff || hasPmDropOff);
            } else if (saStatus.countMode === 'am') {
              // Before 8:30 AM: AM drop-offs at center
              countInRatio = hasAmDropOff;
            } else if (saStatus.countMode === 'am_at_school') {
              // 8:30 AM - 11 AM: AM kids are at school
              countAtSchool = hasAmDropOff;
              countInRatio = false; // Not in ratio, they're at school
            } else {
              // After 11 AM: Reset, PM drop-offs only
              countInRatio = hasPmDropOff;
            }
          }
          
          if (countInRatio) {
            ageCounts.schoolAge++;
            schoolAgeInRatio++;
            totalSignedInCount++;
            schoolAgeTotal++;
          }
          
          if (countAtSchool) {
            schoolAgeAtSchool++;
            schoolAgeTotal++;
          }
        } else {
          // Full Time, Part Time, After School, Regular - Use standard dropOffTime/pickUpTime logic
          const isCurrentlyIn = signin.dropOffTime && !signin.pickUpTime;
          
          if (isCurrentlyIn) {
            totalSignedInCount++;
            
            // Categorize by age for ratio calculations
            if (ageInMonths >= 36) {
              ageCounts.preschool++;
            } else if (ageInMonths >= 24) {
              ageCounts.olderToddler++;
            } else if (ageInMonths >= 12) {
              ageCounts.youngToddler++;
            } else {
              ageCounts.infant++;
            }
          }
        }
      });
      
      setAttendanceStats(prev => ({ 
        ...prev, 
        signedIn: totalSignedInCount,
        schoolAgeTotal,
        schoolAgeInRatio,
        schoolAgeAtSchool,
        overrideActive: schoolAgeAtSchoolOverride !== null,
        overrideMode: schoolAgeAtSchoolOverride
      }));
      
      setChildrenByAge(ageCounts);
    } catch (error) {
      console.error("Error fetching attendance:", error);
    }
  };

  // Recalculate ratios when inputs change
  useEffect(() => {
    calculateRatioCompliance();
  }, [calculateRatioCompliance]);

  useEffect(() => {
    if (hasAccess) {
      getTodayAttendance();
    }
  }, [hasAccess]);

  // Report Generation Functions
  // Report Generation Functions - Fetch real data from API
  const handleGenerateStaffComplianceReport = async () => {
    try {
      toast.info("Generating Staff Compliance Report...");
      const res = await axios.get(`${API}/compliance/staff-compliance-report`);
      const data = res.data;
      const staffList = data.staff || [];
      
      const printWindow = window.open('', '_blank');
      const centerName = organization?.name || 'Childcare Center';
      const logoUrl = 'https://customer-assets.emergentagent.com/job_compliance-dash-27/artifacts/vprevtcd_Simple%20Sign%20In%20Logo.svg';
      
      // Build table rows with ALL certification columns
      const certFields = [
        { key: 'childAbuseClearance', label: 'Child Abuse' },
        { key: 'criminalRecordCheck', label: 'Criminal' },
        { key: 'fbiClearance', label: 'FBI' },
        { key: 'nsorCheck', label: 'NSOR' },
        { key: 'disclosureStatement', label: 'Disclosure' },
        { key: 'mandatedReporterTraining', label: 'Mandated Reporter' },
        { key: 'fireSafetyTraining', label: 'Fire Safety' },
        { key: 'pediatricFirstAidCPR', label: 'First Aid/CPR' },
        { key: 'shakenBabyPolicy', label: 'Shaken Baby' },
        { key: 'profDevTraining', label: 'Prof Dev' },
        { key: 'emergencyPreparedness', label: 'Emergency' },
        { key: 'healthSafetyBasics', label: 'Health & Safety' },
        { key: 'staffEvaluation', label: 'Evaluation' },
        { key: 'staffPhysicalExam', label: 'Physical Exam' },
      ];
      
      const getStatusCell = (cert) => {
        if (!cert) return '<td class="status-missing">– Missing</td>';
        const status = cert.status;
        const date = cert.date || '';
        const exp = cert.expirationDate || '';
        
        if (status === 'complete') {
          return '<td class="status-complete">✓ ' + date + (exp ? '<br/><small>Exp: ' + exp + '</small>' : '') + '</td>';
        } else if (status === 'expiring') {
          return '<td class="status-expiring">⚠ ' + date + '<br/><small>Exp: ' + exp + '</small></td>';
        } else if (status === 'expired') {
          return '<td class="status-expired">✗ Expired<br/><small>' + exp + '</small></td>';
        }
        return '<td class="status-missing">– Missing</td>';
      };
      
      const tableRows = staffList.map(staff => {
        const certs = staff.certifications || {};
        const certCells = certFields.map(f => getStatusCell(certs[f.key])).join('');
        return '<tr>' +
          '<td class="staff-name">' + (staff.name || 'N/A') + '<br/><small>' + (staff.position || 'Staff') + '</small></td>' +
          '<td>' + (staff.hireDate || 'N/A') + '</td>' +
          certCells +
          '<td class="summary-cell ' + (staff.summary.compliancePercent >= 80 ? 'status-complete' : staff.summary.compliancePercent >= 50 ? 'status-expiring' : 'status-missing') + '">' +
            staff.summary.compliancePercent + '%<br/>' +
            '<small>' + staff.summary.complete + '/' + staff.summary.totalItems + '</small>' +
          '</td>' +
          '</tr>';
      }).join('');
      
      printWindow.document.write(
        '<!DOCTYPE html><html><head>' +
        '<title>Staff Compliance Report - ' + centerName + '</title>' +
        '<style>' +
        '@page { size: landscape; margin: 0.5in; }' +
        'body { font-family: Arial, sans-serif; padding: 20px; margin: 0; font-size: 9px; }' +
        '.header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }' +
        '.logo { max-width: 180px; height: auto; margin-bottom: 10px; }' +
        '.center-name { font-size: 14px; color: #333; font-weight: bold; }' +
        '.report-title { font-size: 12px; color: #666; font-weight: bold; margin-top: 5px; }' +
        '.date { font-size: 10px; color: #888; margin-top: 5px; }' +
        '.summary-box { background: #f8f9fa; padding: 10px; border-radius: 5px; margin-bottom: 15px; display: flex; justify-content: space-around; }' +
        '.summary-item { text-align: center; }' +
        '.summary-value { font-size: 18px; font-weight: bold; color: #1a365d; }' +
        '.summary-label { font-size: 9px; color: #666; }' +
        'table { width: 100%; border-collapse: collapse; font-size: 8px; }' +
        'th { background: #1a365d; color: white; padding: 5px 3px; text-align: center; font-size: 7px; white-space: nowrap; }' +
        'td { padding: 4px 2px; border-bottom: 1px solid #ddd; text-align: center; vertical-align: top; }' +
        'tr:nth-child(even) { background: #f9f9f9; }' +
        '.staff-name { text-align: left !important; font-weight: bold; min-width: 100px; }' +
        '.status-complete { color: #28a745; }' +
        '.status-expiring { color: #ffc107; background: #fff8e1; }' +
        '.status-expired { color: #dc3545; background: #ffebee; }' +
        '.status-missing { color: #0d6efd; }' +
        '.summary-cell { font-weight: bold; }' +
        'small { font-size: 7px; color: #666; }' +
        '.footer { margin-top: 15px; text-align: center; font-size: 8px; color: #888; border-top: 1px solid #ddd; padding-top: 10px; }' +
        '.legend { margin-top: 10px; font-size: 8px; }' +
        '.legend span { margin-right: 15px; }' +
        '@media print { body { padding: 10px; } }' +
        '</style></head><body>' +
        '<div class="header">' +
        '<img src="' + logoUrl + '" alt="Simple Sign In" class="logo" />' +
        '<div class="center-name">' + centerName + '</div>' +
        '<div class="report-title">STAFF COMPLIANCE REPORT</div>' +
        '<div class="date">Generated: ' + new Date().toLocaleDateString() + ' at ' + new Date().toLocaleTimeString() + '</div>' +
        '</div>' +
        '<div class="summary-box">' +
        '<div class="summary-item"><div class="summary-value">' + data.summary.totalStaff + '</div><div class="summary-label">Total Staff</div></div>' +
        '<div class="summary-item"><div class="summary-value" style="color:#28a745">' + data.summary.fullyCompliant + '</div><div class="summary-label">Fully Compliant</div></div>' +
        '<div class="summary-item"><div class="summary-value" style="color:#ffc107">' + data.summary.needsAttention + '</div><div class="summary-label">Needs Attention</div></div>' +
        '<div class="summary-item"><div class="summary-value" style="color:#dc3545">' + data.summary.nonCompliant + '</div><div class="summary-label">Non-Compliant</div></div>' +
        '<div class="summary-item"><div class="summary-value">' + data.summary.overallCompliancePercent + '%</div><div class="summary-label">Overall Compliance</div></div>' +
        '</div>' +
        '<table><thead><tr>' +
        '<th style="text-align:left">Staff Name</th>' +
        '<th>Hire Date</th>' +
        certFields.map(f => '<th>' + f.label + '</th>').join('') +
        '<th>Score</th>' +
        '</tr></thead><tbody>' + tableRows + '</tbody></table>' +
        '<div class="legend">' +
        '<strong>Legend:</strong> ' +
        '<span class="status-complete">✓ Complete</span> ' +
        '<span class="status-expiring">⚠ Expiring Soon</span> ' +
        '<span class="status-expired">✗ Expired</span> ' +
        '<span class="status-missing">– Missing</span>' +
        '</div>' +
        '<div class="footer">' +
        '<p>This report is confidential and intended for internal compliance purposes only.</p>' +
        '<p>' + centerName + '</p>' +
        '</div></body></html>'
      );
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error("Error generating staff compliance report:", error);
      toast.error("Failed to generate staff compliance report");
    }
  };

  const handleGenerateChildSummaryReport = async () => {
    try {
      toast.info("Generating Child Records Summary...");
      const res = await axios.get(`${API}/compliance/child-records-summary-report`);
      const data = res.data;
      const childList = data.children || [];
      
      const printWindow = window.open('', '_blank');
      const centerName = organization?.name || 'Childcare Center';
      const logoUrl = 'https://customer-assets.emergentagent.com/job_compliance-dash-27/artifacts/vprevtcd_Simple%20Sign%20In%20Logo.svg';
      
      // Build table rows with ALL form columns
      const formFields = [
        { key: 'enrollment_info', label: 'Enrollment' },
        { key: 'financial_contract', label: 'Financial' },
        { key: 'health_report', label: 'Health Report' },
        { key: 'shot_records', label: 'Shot Records' },
        { key: 'transportation_policy', label: 'Transport' },
        { key: 'about_child', label: 'About Child' },
        { key: 'emergency_consent', label: 'Emergency' },
      ];
      
      const getStatusCell = (form) => {
        if (!form) return '<td class="status-missing">– Missing</td>';
        const status = form.status;
        const date = form.dateOnForm || '';
        const exp = form.expirationDate || '';
        const isOptional = form.optional === true;
        
        if (status === 'complete') {
          return '<td class="status-complete">✓<br/><small>' + date + '</small>' + (exp ? '<br/><small>Exp: ' + exp + '</small>' : '') + '</td>';
        } else if (status === 'expiring') {
          return '<td class="status-expiring">⚠<br/><small>' + date + '</small><br/><small>Exp: ' + exp + '</small></td>';
        } else if (status === 'expired') {
          if (isOptional) return '<td class="status-na">Not On File / Optional</td>';
          return '<td class="status-expired">✗<br/><small>Exp: ' + exp + '</small></td>';
        } else if (status === 'not_applicable') {
          return '<td class="status-na">N/A</td>';
        }
        if (isOptional) return '<td class="status-na">Not On File / Optional</td>';
        return '<td class="status-missing">– Missing</td>';
      };
      
      const tableRows = childList.map(child => {
        const forms = child.forms || {};
        const formCells = formFields.map(f => getStatusCell(forms[f.key])).join('');
        return '<tr>' +
          '<td class="child-name">' + (child.name || 'N/A') + '<br/><small>' + (child.category || '') + '</small></td>' +
          '<td>' + (child.birthdate || 'N/A') + '<br/><small>' + (child.age || '') + '</small></td>' +
          '<td class="parent-info"><small>' + (child.parentName || 'N/A') + '<br/>' + (child.parentPhone || '') + '</small></td>' +
          formCells +
          '<td class="summary-cell ' + (child.summary.compliancePercent >= 80 ? 'status-complete' : child.summary.compliancePercent >= 50 ? 'status-expiring' : 'status-missing') + '">' +
            child.summary.compliancePercent + '%<br/>' +
            '<small>' + child.summary.complete + '/' + child.summary.totalForms + '</small>' +
          '</td>' +
          '</tr>';
      }).join('');
      
      printWindow.document.write(
        '<!DOCTYPE html><html><head>' +
        '<title>Child Records Summary - ' + centerName + '</title>' +
        '<style>' +
        '@page { size: landscape; margin: 0.5in; }' +
        'body { font-family: Arial, sans-serif; padding: 20px; margin: 0; font-size: 9px; }' +
        '.header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }' +
        '.logo { max-width: 180px; height: auto; margin-bottom: 10px; }' +
        '.center-name { font-size: 14px; color: #333; font-weight: bold; }' +
        '.report-title { font-size: 12px; color: #666; font-weight: bold; margin-top: 5px; }' +
        '.date { font-size: 10px; color: #888; margin-top: 5px; }' +
        '.summary-box { background: #f8f9fa; padding: 10px; border-radius: 5px; margin-bottom: 15px; display: flex; justify-content: space-around; }' +
        '.summary-item { text-align: center; }' +
        '.summary-value { font-size: 18px; font-weight: bold; color: #1a365d; }' +
        '.summary-label { font-size: 9px; color: #666; }' +
        'table { width: 100%; border-collapse: collapse; font-size: 8px; }' +
        'th { background: #1a365d; color: white; padding: 5px 3px; text-align: center; font-size: 7px; white-space: nowrap; }' +
        'td { padding: 4px 2px; border-bottom: 1px solid #ddd; text-align: center; vertical-align: top; }' +
        'tr:nth-child(even) { background: #f9f9f9; }' +
        '.child-name { text-align: left !important; font-weight: bold; min-width: 100px; }' +
        '.parent-info { text-align: left !important; min-width: 80px; }' +
        '.status-complete { color: #28a745; }' +
        '.status-expiring { color: #ffc107; background: #fff8e1; }' +
        '.status-expired { color: #dc3545; background: #ffebee; }' +
        '.status-missing { color: #0d6efd; }' +
        '.status-na { color: #6c757d; }' +
        '.summary-cell { font-weight: bold; }' +
        'small { font-size: 7px; color: #666; }' +
        '.footer { margin-top: 15px; text-align: center; font-size: 8px; color: #888; border-top: 1px solid #ddd; padding-top: 10px; }' +
        '.legend { margin-top: 10px; font-size: 8px; }' +
        '.legend span { margin-right: 15px; }' +
        '@media print { body { padding: 10px; } }' +
        '</style></head><body>' +
        '<div class="header">' +
        '<img src="' + logoUrl + '" alt="Simple Sign In" class="logo" />' +
        '<div class="center-name">' + centerName + '</div>' +
        '<div class="report-title">CHILD RECORDS SUMMARY</div>' +
        '<div class="date">Generated: ' + new Date().toLocaleDateString() + ' at ' + new Date().toLocaleTimeString() + '</div>' +
        '<div class="date" style="font-size:8px;color:#999;">Sorted by lowest compliance first</div>' +
        '</div>' +
        '<div class="summary-box">' +
        '<div class="summary-item"><div class="summary-value">' + data.summary.totalChildren + '</div><div class="summary-label">Total Children</div></div>' +
        '<div class="summary-item"><div class="summary-value" style="color:#28a745">' + data.summary.fullyCompliant + '</div><div class="summary-label">Fully Compliant</div></div>' +
        '<div class="summary-item"><div class="summary-value" style="color:#ffc107">' + data.summary.needsAttention + '</div><div class="summary-label">Needs Attention</div></div>' +
        '<div class="summary-item"><div class="summary-value" style="color:#dc3545">' + data.summary.nonCompliant + '</div><div class="summary-label">Non-Compliant</div></div>' +
        '<div class="summary-item"><div class="summary-value">' + data.summary.overallCompliancePercent + '%</div><div class="summary-label">Overall Compliance</div></div>' +
        '</div>' +
        '<table><thead><tr>' +
        '<th style="text-align:left">Child Name</th>' +
        '<th>DOB/Age</th>' +
        '<th style="text-align:left">Parent</th>' +
        formFields.map(f => '<th>' + f.label + '</th>').join('') +
        '<th>Score</th>' +
        '</tr></thead><tbody>' + tableRows + '</tbody></table>' +
        '<div class="legend">' +
        '<strong>Legend:</strong> ' +
        '<span class="status-complete">✓ Complete</span> ' +
        '<span class="status-expiring">⚠ Expiring Soon</span> ' +
        '<span class="status-expired">✗ Expired</span> ' +
        '<span class="status-missing">– Missing</span>' +
        '<span class="status-na">N/A — Not Required</span>' +
        '</div>' +
        '<div class="footer">' +
        '<p>Total Enrolled Children: ' + data.summary.totalChildren + '</p>' +
        '<p>This report is confidential and intended for internal compliance purposes only.</p>' +
        '<p>' + centerName + '</p>' +
        '</div></body></html>'
      );
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error("Error generating child records summary:", error);
      toast.error("Failed to generate child records summary");
    }
  };

  const handleGenerateEmergencyContactList = async () => {
    try {
      // Fetch children with emergency contacts
      const res = await axios.get(`${API}/children`);
      // Exclude inactive children — matches Enrolled Children / dashboard definition
      const childList = (res.data?.children || res.data || []).filter(c => c.active !== false);

      // Pick the two best pickup persons: prioritize relationships that read as
      // parent/mother/father/guardian/custodian, then fall back to whichever
      // pickup contacts are available so the columns aren't left empty.
      const guardianKeywords = ['parent', 'mother', 'father', 'guardian', 'custodian'];
      const getGuardianContacts = (pickupPersons) => {
        const list = pickupPersons || [];
        const prioritized = list.filter(p => guardianKeywords.some(k => (p.relationship || '').toLowerCase().includes(k)));
        const rest = list.filter(p => !prioritized.includes(p));
        const ordered = [...prioritized, ...rest];
        return [ordered[0], ordered[1]];
      };
      
      const printWindow = window.open('', '_blank');
      const centerName = organization?.name || 'Childcare Center';
      const centerAddress = organization?.address || '';
      const logoUrl = 'https://customer-assets.emergentagent.com/job_compliance-dash-27/artifacts/vprevtcd_Simple%20Sign%20In%20Logo.svg';
      
      // Date-only formatter — avoids new Date() timezone shifting a YYYY-MM-DD string
      const formatDobDateOnly = (dateStr) => {
        if (!dateStr) return 'N/A';
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
        if (match) {
          const [, y, m, d] = match;
          return `${m}/${d}/${y}`;
        }
        return dateStr;
      };
      
      let tableRows = childList.map(child => {
        const [guardian1, guardian2] = getGuardianContacts(child.pickupPersons);
        const emergency = child.emergencyContacts?.[0] || {};
        const p1Name = guardian1?.name || 'N/A';
        const p1Phone = guardian1?.phone || 'No phone';
        const p2Name = guardian2?.name || '';
        const p2Phone = guardian2?.phone || '';
        const childDob = formatDobDateOnly(child.birthdate);
        // Combine medical fields without duplicating empty values
        const medicalInfo = [child.allergies, child.medicalEmergencyInfo, child.medicationSpecialConditions, child.specialNeedsNotes]
          .filter(v => v && String(v).trim())
          .join('; ') || 'None noted';
        
        return '<tr>' +
          '<td class="child-name">' + (child.name || 'N/A') + '</td>' +
          '<td>' + childDob + '</td>' +
          '<td class="contact-info"><strong>' + p1Name + '</strong><br/>' + p1Phone + (guardian1?.email ? '<br/>' + guardian1.email : '') + '</td>' +
          '<td class="contact-info"><strong>' + p2Name + '</strong><br/>' + p2Phone + (guardian2?.email ? '<br/>' + guardian2.email : '') + '</td>' +
          '<td class="contact-info emergency"><strong>' + (emergency.name || 'N/A') + '</strong><br/>' + (emergency.phone || emergency.cellPhone || 'No phone') + '<br/>Relation: ' + (emergency.relationship || 'N/A') + '</td>' +
          '<td class="contact-info">' + medicalInfo + '</td>' +
          '</tr>';
      }).join('');
      
      printWindow.document.write(
        '<!DOCTYPE html><html><head>' +
        '<title>Emergency Contact List - ' + centerName + '</title>' +
        '<style>' +
        'body { font-family: Arial, sans-serif; padding: 30px; max-width: 1000px; margin: 0 auto; }' +
        '.header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #dc3545; padding-bottom: 15px; }' +
        '.logo { max-width: 180px; height: auto; margin-bottom: 10px; }' +
        '.center-name { font-size: 18px; color: #333; margin-bottom: 5px; font-weight: bold; }' +
        '.report-title { font-size: 16px; color: #dc3545; font-weight: bold; }' +
        '.state-req { font-size: 11px; color: #666; font-style: italic; margin-top: 5px; }' +
        '.date { font-size: 12px; color: #888; margin-top: 10px; }' +
        'table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 10px; }' +
        'th { background: #dc3545; color: white; padding: 8px; text-align: left; }' +
        'td { padding: 6px 8px; border-bottom: 1px solid #ddd; vertical-align: top; }' +
        'tr:nth-child(even) { background: #fff5f5; }' +
        '.child-name { font-weight: bold; }' +
        '.contact-info { font-size: 9px; }' +
        '.emergency { color: #dc3545; }' +
        '.footer { margin-top: 20px; text-align: center; font-size: 10px; color: #888; border-top: 1px solid #ddd; padding-top: 10px; }' +
        '.first-aid-note { background: #fff3cd; padding: 10px; border-radius: 5px; margin-top: 15px; font-size: 11px; }' +
        '@media print { body { padding: 15px; } .first-aid-note { page-break-inside: avoid; } }' +
        '</style></head><body>' +
        '<div class="header">' +
        '<img src="' + logoUrl + '" alt="Simple Sign In" class="logo" />' +
        '<div class="center-name">' + centerName + '</div>' +
        '<div class="report-title">EMERGENCY CONTACT LIST</div>' +
        '<div class="state-req">State Requirement - Must be kept in First Aid Kit</div>' +
        '<div class="date">Generated: ' + new Date().toLocaleDateString() + ' at ' + new Date().toLocaleTimeString() + '</div>' +
        '</div>' +
        '<table><thead><tr>' +
        '<th style="width: 15%">Child Name</th>' +
        '<th style="width: 8%">DOB</th>' +
        '<th style="width: 20%">Parent/Guardian 1</th>' +
        '<th style="width: 20%">Parent/Guardian 2</th>' +
        '<th style="width: 20%">Emergency Contact</th>' +
        '<th style="width: 17%">Medical/Allergies</th>' +
        '</tr></thead><tbody>' + tableRows + '</tbody></table>' +
        '<div class="first-aid-note"><strong>FIRST AID KIT REQUIREMENT:</strong> This Emergency Contact List must be kept in the First Aid Kit and updated regularly per state licensing requirements.</div>' +
        '<div class="footer">' +
        '<p>Total Children: ' + childList.length + ' | This list is confidential - for emergency use only</p>' +
        '<p>' + centerName + ' | ' + centerAddress + '</p>' +
        '</div></body></html>'
      );
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error("Error generating emergency contact list:", error);
      toast.error("Failed to generate emergency contact list");
    }
  };

  // Reusable focused print report for a single compliance category — reuses the
  // exact same complianceItems/complianceStatus/readinessScore data already
  // powering the checklist and alert cards. No new status logic, no file content.
  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const generateComplianceCategoryReport = (category, reportTitle) => {
    try {
      const items = (complianceItems || []).filter(i => i.category === category && i.isEnabled !== false);
      const pct = category === 'facility_documents' ? (readinessScore?.facilityScore || 0) : (readinessScore?.emergencyScore || 0);

      const formatDateOnly = (dateStr) => {
        if (!dateStr) return '';
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
        if (match) { const [, y, m, d] = match; return `${m}/${d}/${y}`; }
        return dateStr;
      };
      const statusLabel = (s) => s === 'complete' ? 'Current' : s === 'expiring' ? 'Expiring Soon' : s === 'expired' ? 'Expired' : '– Missing';
      const statusClass = (s) => s === 'complete' ? 'status-complete' : s === 'expiring' ? 'status-expiring' : s === 'expired' ? 'status-expired' : 'status-missing';

      const counts = { complete: 0, expiring: 0, expired: 0, missing: 0 };
      items.forEach(i => { counts[i.complianceStatus] = (counts[i.complianceStatus] || 0) + 1; });

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Please allow pop-ups for this site to view the report");
        return;
      }
      const centerName = escapeHtml(organization?.name || 'Childcare Center');
      const logoUrl = 'https://customer-assets.emergentagent.com/job_compliance-dash-27/artifacts/vprevtcd_Simple%20Sign%20In%20Logo.svg';

      const tableRows = items.map(item => {
        const docIndicator = item.hasUploadedDocument ? 'Document on file' : item.hasPaperCopy ? 'Paper copy on file' : 'None on file';
        return '<tr>' +
          '<td class="item-name">' + escapeHtml(item.name || 'N/A') + '</td>' +
          '<td class="' + statusClass(item.complianceStatus) + '">' + statusLabel(item.complianceStatus) + '</td>' +
          '<td>' + (formatDateOnly(item.expirationDate) || 'N/A') + '</td>' +
          '<td>' + docIndicator + '</td>' +
          '</tr>';
      }).join('');

      printWindow.document.write(
        '<!DOCTYPE html><html><head>' +
        '<title>' + reportTitle + ' - ' + centerName + '</title>' +
        '<style>' +
        'body { font-family: Arial, sans-serif; padding: 30px; max-width: 900px; margin: 0 auto; }' +
        '.header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #1a365d; padding-bottom: 15px; }' +
        '.logo { max-width: 180px; height: auto; margin-bottom: 10px; }' +
        '.center-name { font-size: 16px; color: #333; font-weight: bold; }' +
        '.report-title { font-size: 14px; color: #1a365d; font-weight: bold; margin-top: 5px; }' +
        '.date { font-size: 11px; color: #888; margin-top: 5px; }' +
        '.summary-box { background: #f8f9fa; padding: 12px; border-radius: 5px; margin: 15px 0; display: flex; justify-content: space-around; }' +
        '.summary-item { text-align: center; }' +
        '.summary-value { font-size: 20px; font-weight: bold; color: #1a365d; }' +
        '.summary-label { font-size: 10px; color: #666; }' +
        'table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }' +
        'th { background: #1a365d; color: white; padding: 6px 8px; text-align: left; }' +
        'td { padding: 6px 8px; border-bottom: 1px solid #ddd; }' +
        'tr:nth-child(even) { background: #f9f9f9; }' +
        '.item-name { font-weight: bold; }' +
        '.status-complete { color: #28a745; font-weight: bold; }' +
        '.status-expiring { color: #856404; background: #fff3cd; font-weight: bold; }' +
        '.status-expired { color: #dc3545; background: #ffebee; font-weight: bold; }' +
        '.status-missing { color: #0d6efd; font-weight: bold; }' +
        '.footer { margin-top: 20px; text-align: center; font-size: 10px; color: #888; border-top: 1px solid #ddd; padding-top: 10px; }' +
        '@media print { body { padding: 15px; } }' +
        '</style></head><body>' +
        '<div class="header">' +
        '<img src="' + logoUrl + '" alt="Simple Sign In" class="logo" />' +
        '<div class="center-name">' + centerName + '</div>' +
        '<div class="report-title">' + reportTitle.toUpperCase() + '</div>' +
        '<div class="date">Generated: ' + new Date().toLocaleDateString() + ' at ' + new Date().toLocaleTimeString() + '</div>' +
        '</div>' +
        '<div class="summary-box">' +
        '<div class="summary-item"><div class="summary-value">' + pct + '%</div><div class="summary-label">Category Compliance</div></div>' +
        '<div class="summary-item"><div class="summary-value" style="color:#28a745">' + counts.complete + '</div><div class="summary-label">Current</div></div>' +
        '<div class="summary-item"><div class="summary-value" style="color:#856404">' + counts.expiring + '</div><div class="summary-label">Expiring</div></div>' +
        '<div class="summary-item"><div class="summary-value" style="color:#dc3545">' + (counts.expired + counts.missing) + '</div><div class="summary-label">Missing/Expired</div></div>' +
        '</div>' +
        '<table><thead><tr><th>Item</th><th>Status</th><th>Due/Expiration Date</th><th>Documentation</th></tr></thead><tbody>' + tableRows + '</tbody></table>' +
        '<div class="footer">' +
        '<p>' + items.length + ' total items in this category | This report is confidential and intended for internal compliance purposes only.</p>' +
        '<p>' + centerName + '</p>' +
        '</div></body></html>'
      );
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error("Error generating " + reportTitle + ":", error);
      toast.error("Failed to generate " + reportTitle);
    }
  };

  // If showing Inspection Mode view
  if (showInspectionMode) {
    return (
      <AppShell pageTitle="Center Compliance Dashboard" breadcrumb>
        <div className="space-y-4">
          <Button 
            variant="outline" 
            onClick={() => setShowInspectionMode(false)}
            className="mb-4"
          >
            ← Back to Dashboard
          </Button>
          <InspectionMode
            inspectionMode={inspectionMode}
            readinessScore={readinessScore}
            alerts={alerts}
            onUpdate={handleUpdate}
            staffOnDuty={staffOnDuty}
            childrenByAge={childrenByAge}
            isNapTime={isNapTime}
            attendanceCount={attendanceStats.signedIn}
            scrollToBinder={scrollToBinder}
            onScrollComplete={() => setScrollToBinder(false)}
          />
        </div>
      </AppShell>
    );
  }

  if (loading) {
    return (
      <AppShell pageTitle="Center Compliance Dashboard" breadcrumb>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#2b3d4f] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading compliance data...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  const score = readinessScore?.score || 0;
  const complianceLevel = score >= 90 ? 'Mostly Compliant' : score >= 70 ? 'Needs Attention' : 'Critical';

  return (
    <AppShell pageTitle="Center Compliance Dashboard" breadcrumb>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.compliance}
        title="Compliance Dashboard"
        subtitle="Stay inspection-ready with real-time compliance tracking"
        altText="Daycare compliance inspection"
      />
      
      <FeatureGate feature="compliance_dashboard">
      <div className="relative z-10 space-y-6">
        {/* Print Header - Only visible when printing */}
        <PrintReportHeader reportTitle="Compliance Dashboard" />
        
        {/* Header with Organization Selector */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-700 flex items-center gap-2">
            {organization?.name || 'Simple Sign In'}
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </h2>
        </div>

        {/* Staff compliance visibility — managed from Home Dashboard next to the % badge */}

        {/* Inspection Readiness Header Bar - Deep Navy for importance */}
        <div 
          className="bg-[#1a365d] rounded-lg p-6"
          data-testid="inspection-readiness-bar"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-white text-2xl font-bold flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <ClipboardCheck className="w-6 h-6 text-white" />
                </div>
                Inspection Readiness: {score}%
              </h1>
              <p className="text-white/80 mt-1 ml-15">
                {complianceLevel} • State Licensing Compliance
              </p>
            </div>
            <div className={`px-4 py-2 rounded-lg font-bold text-lg ${
              score >= 90 ? 'bg-green-500 text-white' : 
              score >= 70 ? 'bg-yellow-500 text-gray-900' : 
              'bg-red-500 text-white'
            }`}>
              {score}%
            </div>
          </div>
        </div>

        {/* Score Breakdown — collapsible */}
        {readinessScore && (() => {
          const facilityPct  = readinessScore.facilityScore  || 0;
          const staffPct     = readinessScore.staffScore     || 0;
          const childrenPct  = readinessScore.childrenScore  || 0;
          const emergencyPct = readinessScore.emergencyScore || 0;
          const facTotalW    = readinessScore.totalWeight    || 1;
          const emergencyTotalW = readinessScore.emergencyStats?.total || 1;
          const staffTotal   = readinessScore.staffStats?.total    || 1;
          const childTotal   = readinessScore.childrenStats?.total || 1;

          const pillars = [
            { label: "Staff Compliance",   pct: staffPct,     max: 30, earned: +(staffPct     * 0.30).toFixed(1) },
            { label: "Children Records",   pct: childrenPct,  max: 30, earned: +(childrenPct  * 0.30).toFixed(1) },
            { label: "Facility Documents", pct: facilityPct,  max: 25, earned: +(facilityPct  * 0.25).toFixed(1) },
            { label: "Emergency Preparedness", pct: emergencyPct, max: 15, earned: +(emergencyPct * 0.15).toFixed(1) },
          ];

          const issues = [];
          ;(alerts || []).forEach(a => {
            // Children/Staff issues are listed from their own detail arrays below —
            // skip them here so nothing is double-listed in Top Issues.
            if (a.category === 'children_records' || a.category === 'staff_records') return;
            const isEmergency = a.category === 'emergency_preparedness';
            const cat = isEmergency ? 'Emergency' : 'Facility';
            const denom = isEmergency ? emergencyTotalW : facTotalW;
            const maxPts = isEmergency ? 15 : 25;
            if (a.status === "missing" || a.status === "expired") {
              issues.push({ name: a.itemName, category: cat, status: a.status, impact: +((a.weight / denom) * maxPts).toFixed(1), ...(a.missingCount > 1 ? { count: a.missingCount } : {}) });
            } else if (a.status === "expiring") {
              issues.push({ name: a.itemName, category: cat, status: "expiring", impact: +((a.weight / denom) * maxPts * 0.5).toFixed(1), ...(a.missingCount > 1 ? { count: a.missingCount } : {}) });
            }
          });
          const staffCertCount = {};
          ;(readinessScore.staffMissingDetails || []).forEach(s =>
            (s.missingCerts || []).forEach(c => { const k = c.replace(" (Expired)",""); staffCertCount[k] = (staffCertCount[k]||0)+1; })
          );
          Object.entries(staffCertCount).forEach(([cert, count]) =>
            issues.push({ name: cert, category: "Staff", status: "missing", impact: +((count / staffTotal) * 30).toFixed(1), count })
          );
          const childFormCount = {};
          const childExpiredFormCount = {};
          ;(readinessScore.childrenMissingDetails || []).forEach(c =>
            (c.missingForms || []).forEach(f => {
              if (f.endsWith(" (Expired)")) {
                const k = f.replace(" (Expired)", "");
                childExpiredFormCount[k] = (childExpiredFormCount[k] || 0) + 1;
              } else {
                childFormCount[f] = (childFormCount[f] || 0) + 1;
              }
            })
          );
          Object.entries(childExpiredFormCount).forEach(([form, count]) =>
            issues.push({ name: form, category: "Children", status: "expired", impact: +((count / childTotal) * 30).toFixed(1), count })
          );
          Object.entries(childFormCount).forEach(([form, count]) =>
            issues.push({ name: form, category: "Children", status: "missing", impact: +((count / childTotal) * 30).toFixed(1), count })
          );
          issues.sort((a, b) => b.impact - a.impact);
          const topIssues = issues.slice(0, 8);

          const barColor = (p) => p >= 80 ? "bg-green-500" : p >= 50 ? "bg-amber-500" : "bg-red-500";
          const txtColor = (p) => p >= 80 ? "text-green-600" : p >= 50 ? "text-amber-600" : "text-red-600";
          const statusBadge = (s) => {
            if (s === "expired")  return <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">Expired</span>;
            if (s === "expiring") return <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">Expiring</span>;
            return <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">Missing</span>;
          };

          return (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <button
                onClick={() => setShowScoreBreakdown(v => !v)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                data-testid="score-breakdown-toggle"
              >
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-gray-800">See What's Affecting Your Score</span>
                </div>
                {showScoreBreakdown ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </button>

              {showScoreBreakdown && (
                <div className="px-5 pb-5 space-y-6 border-t border-gray-100">
                  {/* Part 1 — Weighted Contribution */}
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">How your score is built</p>
                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-[#1a365d] text-white">
                            <th className="text-left px-4 py-2.5 font-semibold">Category</th>
                            <th className="text-center px-4 py-2.5 font-semibold">% Complete</th>
                            <th className="text-center px-4 py-2.5 font-semibold">Max Points</th>
                            <th className="text-center px-4 py-2.5 font-semibold">Points Earned</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pillars.map((p, i) => (
                            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                              <td className="px-4 py-3 font-medium text-gray-800">{p.label}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2 justify-center">
                                  <div className="w-24 bg-gray-200 rounded-full h-2">
                                    <div className={`h-2 rounded-full ${barColor(p.pct)}`} style={{ width: `${p.pct}%` }} />
                                  </div>
                                  <span className={`font-semibold text-xs w-12 text-right ${txtColor(p.pct)}`}>{p.pct.toFixed(1)}%</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center text-gray-500">{p.max}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`font-bold ${txtColor(p.pct)}`}>{p.earned}</span>
                                <span className="text-gray-400 text-xs"> / {p.max}</span>
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-[#1a365d] text-white font-bold">
                            <td className="px-4 py-3">Total Score</td>
                            <td className="px-4 py-3 text-center">—</td>
                            <td className="px-4 py-3 text-center">100</td>
                            <td className="px-4 py-3 text-center">{score} / 100</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Part 2 — Top Issues */}
                  {topIssues.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-3">Top issues costing you points <span className="font-normal text-gray-400">(highest impact first)</span></p>
                      <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="text-left px-4 py-2.5 text-gray-600 font-semibold">Item</th>
                              <th className="text-center px-4 py-2.5 text-gray-600 font-semibold">Area</th>
                              <th className="text-center px-4 py-2.5 text-gray-600 font-semibold">Status</th>
                              <th className="text-center px-4 py-2.5 text-gray-600 font-semibold">Points Lost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topIssues.map((issue, i) => (
                              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                <td className="px-4 py-2.5 font-medium text-gray-800">
                                  {issue.name}
                                  {issue.count > 1 && <span className="ml-1.5 text-xs text-gray-400">({issue.count} {issue.category === "Staff" ? "staff" : "children"})</span>}
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                                    issue.category === "Facility"  ? "bg-blue-50 text-blue-700 border-blue-200" :
                                    issue.category === "Staff"     ? "bg-purple-50 text-purple-700 border-purple-200" :
                                    issue.category === "Emergency" ? "bg-rose-50 text-rose-700 border-rose-200" :
                                                                     "bg-teal-50 text-teal-700 border-teal-200"
                                  }`}>{issue.category}</span>
                                </td>
                                <td className="px-4 py-2.5 text-center">{statusBadge(issue.status)}</td>
                                <td className="px-4 py-2.5 text-center font-bold text-red-600">−{issue.impact} pts</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* 4 Main Cards - 2x2 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Staff Compliance Card - Red accent for urgency */}
          <Card className="bg-white shadow-md border-l-4 border-l-[#dc3545]" data-testid="staff-compliance-card">
            <CardHeader className="pb-2 bg-gradient-to-r from-red-50 to-white">
              <CardTitle className="text-lg font-bold text-[#1a365d] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#dc3545]" />
                  Staff Compliance
                </div>
                <Button 
                  onClick={() => navigate('/staff-records')}
                  className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white text-xs px-3 py-1 h-auto font-semibold shadow-sm"
                  data-testid="fix-staff-issues-btn"
                >
                  Fix Issues
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <span className="text-5xl font-bold text-[#1a365d]">{staffStats.total}</span>
                  <span className="text-gray-600 ml-2 text-sm">Total Staff</span>
                </div>
                <div className="flex flex-col gap-2">
                  <Badge className="bg-[#fff3cd] hover:bg-[#fff3cd] text-[#856404] border border-[#ffc107] justify-start gap-2 py-2 px-3 font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    {staffStats.expiring} Expiring Soon
                  </Badge>
                  <Badge className="bg-[#f8d7da] hover:bg-[#f8d7da] text-[#721c24] border border-[#dc3545] justify-start gap-2 py-2 px-3 font-medium">
                    <XCircle className="w-4 h-4" />
                    {staffStats.actionNeeded} Action Needed
                  </Badge>
                </div>
                <Button 
                  onClick={handleGenerateStaffComplianceReport}
                  className="w-full bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-semibold"
                  data-testid="view-staff-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  View Missing Documents
                </Button>
                
                {/* Staff Missing Records Summary */}
                {readinessScore?.staffMissingDetails?.length > 0 && (
                  <div className="border-t pt-3 mt-2">
                    <p className="text-xs font-semibold text-red-700 mb-2">Missing/Expired Records:</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {(showAllStaff ? readinessScore.staffMissingDetails : readinessScore.staffMissingDetails.slice(0, 4)).map((staff, idx) => (
                        <div key={idx} className="text-xs bg-red-50 rounded p-1.5">
                          <p className="font-medium text-gray-800 truncate">{staff.staffName}</p>
                          <p className="text-red-600">
                            {expandedStaffItems[idx] 
                              ? staff.missingCerts?.join(', ')
                              : (
                                <>
                                  {staff.missingCerts?.slice(0, 2).join(', ')}
                                  {staff.totalMissing > 2 && (
                                    <button 
                                      onClick={() => setExpandedStaffItems(prev => ({...prev, [idx]: true}))}
                                      className="ml-1 text-blue-600 hover:text-blue-800 font-semibold underline cursor-pointer"
                                    >
                                      +{staff.totalMissing - 2} more
                                    </button>
                                  )}
                                </>
                              )
                            }
                            {expandedStaffItems[idx] && staff.totalMissing > 2 && (
                              <button 
                                onClick={() => setExpandedStaffItems(prev => ({...prev, [idx]: false}))}
                                className="ml-1 text-blue-600 hover:text-blue-800 font-semibold underline cursor-pointer"
                              >
                                (show less)
                              </button>
                            )}
                          </p>
                        </div>
                      ))}
                      {readinessScore.staffMissingDetails.length > 4 && (
                        <button 
                          onClick={() => setShowAllStaff(!showAllStaff)}
                          className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold underline cursor-pointer w-full text-center py-1"
                        >
                          {showAllStaff 
                            ? 'Show less staff' 
                            : `+${readinessScore.staffMissingDetails.length - 4} more staff...`
                          }
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Child Records Card - Orange accent */}
          <Card className="bg-white shadow-md border-l-4 border-l-[#fd7e14]" data-testid="child-records-card">
            <CardHeader className="pb-2 bg-gradient-to-r from-orange-50 to-white">
              <CardTitle className="text-lg font-bold text-[#1a365d] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Baby className="w-5 h-5 text-[#fd7e14]" />
                  Child Records
                </div>
                <Button 
                  onClick={() => navigate('/child-records')}
                  className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white text-xs px-3 py-1 h-auto font-semibold shadow-sm"
                  data-testid="fix-child-issues-btn"
                >
                  Fix Issues
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <span className="text-5xl font-bold text-[#1a365d]">{childStats.total}</span>
                  <span className="text-gray-600 ml-2 text-sm">Total Children</span>
                </div>
                <div className="flex flex-col gap-2">
                  <Badge className="bg-[#fff3cd] hover:bg-[#fff3cd] text-[#856404] border border-[#ffc107] justify-start gap-2 py-2 px-3 font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    {childStats.expiring} Expiring Soon
                  </Badge>
                  <Badge className="bg-[#f8d7da] hover:bg-[#f8d7da] text-[#721c24] border border-[#dc3545] justify-start gap-2 py-2 px-3 font-medium" data-testid="child-expired-badge">
                    <XCircle className="w-4 h-4" />
                    {childStats.childrenWithExpired || childStats.expired || 0} Expired
                  </Badge>
                  <Badge className="bg-[#f8d7da] hover:bg-[#f8d7da] text-[#721c24] border border-[#dc3545] justify-start gap-2 py-2 px-3 font-medium">
                    <XCircle className="w-4 h-4" />
                    {childStats.childrenWithMissing || childStats.missing} Missing Documents
                  </Badge>
                </div>
                <Button 
                  onClick={handleGenerateChildSummaryReport}
                  className="w-full bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-semibold"
                  data-testid="view-child-files-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  View Missing Documents
                </Button>
                
                {/* Children Missing Forms Summary */}
                {readinessScore?.childrenMissingDetails?.length > 0 && (
                  <div className="border-t pt-3 mt-2">
                    <p className="text-xs font-semibold text-orange-700 mb-2">Forms Needing Attention:</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {(showAllChildren ? readinessScore.childrenMissingDetails : readinessScore.childrenMissingDetails.slice(0, 4)).map((child, idx) => {
                        const formsToShow = expandedChildItems[idx] ? child.missingForms : child.missingForms?.slice(0, 2);
                        return (
                        <div key={idx} className="text-xs bg-orange-50 rounded p-1.5">
                          <p className="font-medium text-gray-800 truncate">{child.childName}</p>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {formsToShow?.map((f, fi) => {
                              const isExpired = f.endsWith(' (Expired)');
                              const label = isExpired ? f.replace(' (Expired)', '') : f;
                              return (
                                <span key={fi} className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${isExpired ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                                  {label}{isExpired && <span className="ml-1 opacity-75">· Expired</span>}
                                </span>
                              );
                            })}
                            {!expandedChildItems[idx] && child.totalMissing > 2 && (
                              <button
                                onClick={() => setExpandedChildItems(prev => ({...prev, [idx]: true}))}
                                className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold underline cursor-pointer"
                              >
                                +{child.totalMissing - 2} more
                              </button>
                            )}
                            {expandedChildItems[idx] && child.totalMissing > 2 && (
                              <button
                                onClick={() => setExpandedChildItems(prev => ({...prev, [idx]: false}))}
                                className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold underline cursor-pointer"
                              >
                                show less
                              </button>
                            )}
                          </div>
                        </div>
                        );
                      })}
                      {readinessScore.childrenMissingDetails.length > 4 && (
                        <button 
                          onClick={() => setShowAllChildren(!showAllChildren)}
                          className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold underline cursor-pointer w-full text-center py-1"
                        >
                          {showAllChildren 
                            ? 'Show less children' 
                            : `+${readinessScore.childrenMissingDetails.length - 4} more children...`
                          }
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Attendance Status Card with Staff Ratio Calculator */}
          <Card className="bg-white shadow-md border-l-4 border-l-[#28a745]" data-testid="attendance-status-card">
            <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-white">
              <CardTitle className="text-lg font-bold text-[#1a365d] flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-[#28a745]" />
                Attendance & Ratios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Current Attendance */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-4xl font-bold text-[#1a365d]">{attendanceStats.signedIn}</span>
                    <span className="text-gray-600 ml-2 text-xs">Signed In</span>
                  </div>
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowEnrolledReport(true)}
                    className="text-[#1a365d] text-xs"
                    data-testid="enrolled-report-btn"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    Enrolled
                  </Button>
                </div>
                
                {/* Staff On Duty Input */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold text-gray-600">Staff On Duty:</Label>
                    <Input
                      type="number"
                      min="0"
                      value={staffOnDuty}
                      onChange={(e) => setStaffOnDuty(e.target.value)}
                      className="w-16 h-7 text-center text-sm"
                      data-testid="staff-on-duty-input"
                    />
                  </div>
                  
                  {/* Nap Time Toggle - Doubles Ratios */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Moon className="w-3 h-3 text-purple-500" />
                      <Label className="text-xs text-gray-600">Nap Time (2x ratios)</Label>
                    </div>
                    <Switch
                      checked={isNapTime}
                      onCheckedChange={setIsNapTime}
                      data-testid="nap-time-toggle"
                    />
                  </div>
                </div>
                
                {/* Ratio Compliance Result */}
                {ratioCompliance && (
                  <div className={`rounded-lg p-2 ${ratioCompliance.compliant ? 'bg-green-50 border border-green-300' : 'bg-red-50 border border-red-300'}`}>
                    <div className="flex items-center gap-2">
                      {ratioCompliance.compliant ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`text-sm font-semibold ${ratioCompliance.compliant ? 'text-green-700' : 'text-red-700'}`}>
                        {ratioCompliance.compliant ? 'COMPLIANT' : 'NON-COMPLIANT'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {ratioCompliance.staffOnDuty} staff for {ratioCompliance.totalChildren} children
                      {ratioCompliance.isMixedAgeGroup && (
                        <span className="text-purple-600"> (Mixed ages: {ratioCompliance.activeAgeGroups} groups)</span>
                      )}
                    </p>
                    {/* Weighted Slots Breakdown */}
                    {ratioCompliance.isMixedAgeGroup && (
                      <div className="mt-2 p-1.5 bg-white/50 rounded text-[10px]">
                        <p className="font-semibold text-purple-700 mb-1">Weighted Slots Method:</p>
                        {ratioCompliance.details?.filter(d => !d.excluded).map((detail, idx) => (
                          <div key={idx} className="flex justify-between text-gray-600">
                            <span>{detail.ageGroup}: {detail.children} × {detail.ratio}</span>
                            <span className="text-purple-600 font-medium">= {detail.weightedSlots}</span>
                          </div>
                        ))}
                        <div className="flex justify-between border-t border-purple-200 mt-1 pt-1 font-semibold">
                          <span>Total weighted slots:</span>
                          <span className="text-purple-700">{ratioCompliance.totalWeightedSlots} → {ratioCompliance.requiredStaff} staff</span>
                        </div>
                      </div>
                    )}
                    {!ratioCompliance.compliant && (
                      <p className="text-xs text-red-600 font-medium mt-1">
                        Need {ratioCompliance.requiredStaff} staff minimum
                      </p>
                    )}
                  </div>
                )}
                
                {/* School-Age Status Indicator with Override Toggle */}
                {schoolAgeStatus && (
                  <div className={`rounded-lg p-2 text-xs ${
                    attendanceStats.overrideActive
                      ? 'bg-purple-50 border border-purple-300'
                      : schoolAgeStatus.isSummer 
                        ? 'bg-yellow-50 border border-yellow-300' 
                        : schoolAgeStatus.countMode === 'pm'
                          ? 'bg-blue-50 border border-blue-300'
                          : 'bg-green-50 border border-green-300'
                  }`} data-testid="school-age-status">
                    {/* Header with status */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="font-semibold text-gray-700">School-Age Status:</span>
                      <span className={`font-medium text-[11px] ${
                        attendanceStats.overrideActive
                          ? 'text-purple-700'
                          : schoolAgeStatus.isSummer 
                            ? 'text-yellow-700' 
                            : 'text-blue-700'
                      }`}>
                        {attendanceStats.overrideActive
                          ? (attendanceStats.overrideMode ? '📚 At School (Override)' : '🏠 At Center (Override)')
                          : schoolAgeStatus.isSummer 
                            ? 'Summer - All day'
                            : 'School hours - count PM returns only'}
                      </span>
                    </div>
                    
                    {/* Override Toggle Buttons */}
                    <div className="flex items-center gap-1 mb-2 p-1.5 bg-white rounded border">
                      <button
                        onClick={() => setSchoolAgeAtSchoolOverride(null)}
                        className={`flex-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                          schoolAgeAtSchoolOverride === null
                            ? 'bg-gray-700 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        data-testid="school-age-auto-btn"
                      >
                        🕐 Auto
                      </button>
                      <button
                        onClick={() => setSchoolAgeAtSchoolOverride(true)}
                        className={`flex-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                          schoolAgeAtSchoolOverride === true
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        data-testid="school-age-at-school-btn"
                      >
                        📚 At School
                      </button>
                      <button
                        onClick={() => setSchoolAgeAtSchoolOverride(false)}
                        className={`flex-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                          schoolAgeAtSchoolOverride === false
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        data-testid="school-age-at-center-btn"
                      >
                        🏠 At Center
                      </button>
                    </div>
                    
                    {/* Show actual counts */}
                    {(attendanceStats.schoolAgeTotal > 0 || childrenByAge.schoolAge > 0 || attendanceStats.schoolAgeAtSchool > 0) ? (
                      <div className="space-y-0.5">
                        {childrenByAge.schoolAge > 0 && (
                          <p className="text-green-600">
                            ✓ {childrenByAge.schoolAge} school-age at center
                          </p>
                        )}
                        {attendanceStats.schoolAgeAtSchool > 0 && (
                          <p className="text-blue-600">
                            📚 {attendanceStats.schoolAgeAtSchool} at school (not in ratio)
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500">No school-age signed in</p>
                    )}
                    
                    {/* Info about the current mode */}
                    <p className="text-gray-400 mt-1 italic text-[9px]">
                      {attendanceStats.overrideActive
                        ? 'Manual override active - tap Auto to use time-based logic'
                        : schoolAgeStatus.isSummer 
                          ? 'Summer (Jun-Aug): All school-age counted'
                          : 'After 8:30 AM: Only PM returns counted'}
                    </p>
                  </div>
                )}
                
                {/* Ratio Reference */}
                <div className="text-[9px] text-gray-400 border-t pt-2">
                  <p className="font-medium mb-1">Ratios{isNapTime ? ' (Nap Time 2x)' : ''}:</p>
                  <div className="grid grid-cols-2 gap-x-2">
                    <span>Infant: 1:{isNapTime ? '8' : '4'}</span>
                    <span>Y.Toddler: 1:{isNapTime ? '10' : '5'}</span>
                    <span>O.Toddler: 1:{isNapTime ? '12' : '6'}</span>
                    <span>Preschool: 1:{isNapTime ? '20' : '10'}</span>
                    <span>School Age: 1:{isNapTime ? '30' : '15'}</span>
                  </div>
                  <p className="mt-1 italic">*Mixed groups use weighted slots method</p>
                </div>
                
                <Button 
                  onClick={() => navigate(`/signin/${format(new Date(), 'yyyy-MM-dd')}`)}
                  className="w-full bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-semibold"
                  data-testid="view-attendance-btn"
                >
                  View Attendance <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Meal Compliance Tracker Card - Blue accent, separate from inspection rating */}
          <Card className="bg-white shadow-md border-l-4 border-l-[#17a2b8]" data-testid="meal-compliance-card">
            <CardHeader className="pb-2 bg-gradient-to-r from-cyan-50 to-white">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold text-[#1a365d] flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-[#17a2b8]" />
                  Meal Compliance Tracker
                </CardTitle>
                <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                  Separate Agency
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Today's Meals */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Today</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-blue-50 rounded p-2">
                      <p className="text-lg font-bold text-[#1a365d]">{cacfpStats.todayMeals.breakfast}</p>
                      <p className="text-[10px] text-gray-500">Breakfast</p>
                    </div>
                    <div className="bg-blue-50 rounded p-2">
                      <p className="text-lg font-bold text-[#1a365d]">{cacfpStats.todayMeals.lunch}</p>
                      <p className="text-[10px] text-gray-500">Lunch</p>
                    </div>
                    <div className={`rounded p-2 ${cacfpStats.snackMissing ? 'bg-yellow-50 border border-yellow-300' : 'bg-blue-50'}`}>
                      <p className="text-lg font-bold text-[#1a365d]">{cacfpStats.todayMeals.snack}</p>
                      <p className="text-[10px] text-gray-500">Snack</p>
                    </div>
                  </div>
                </div>
                
                {/* Month to Date */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Month to Date</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-gray-50 rounded p-1.5">
                      <p className="text-sm font-semibold text-gray-700">{cacfpStats.monthToDate.breakfast}</p>
                    </div>
                    <div className="bg-gray-50 rounded p-1.5">
                      <p className="text-sm font-semibold text-gray-700">{cacfpStats.monthToDate.lunch}</p>
                    </div>
                    <div className="bg-gray-50 rounded p-1.5">
                      <p className="text-sm font-semibold text-gray-700">{cacfpStats.monthToDate.snack}</p>
                    </div>
                  </div>
                </div>

                {cacfpStats.snackMissing && (
                  <div className="flex items-center gap-2 text-[#856404] bg-[#fff3cd] px-2 py-1.5 rounded text-xs">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>Today's Snack Not Logged</span>
                  </div>
                )}
                
                {/* Note about separate agency */}
                <p className="text-[10px] text-gray-400 italic border-t pt-2 mt-2">
                  *CACFP regulated by USDA/DHS - Not included in state licensing inspection score
                </p>
                
                <Button 
                  onClick={() => navigate('/cacfp')}
                  className="w-full bg-[#17a2b8] hover:bg-[#138496] text-white font-semibold"
                >
                  Open Meal Tracker <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Three Alert Sections + Center Alerts */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Staff Compliance Alerts - Real data from deadlines */}
          <Card className="bg-white shadow-md border-t-4 border-t-[#dc3545]" data-testid="staff-alerts-section">
            <CardHeader className="pb-2 bg-gradient-to-b from-red-50 to-white">
              <CardTitle className="text-base font-bold text-[#1a365d] flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-[#dc3545]" />
                Staff Compliance Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-36 overflow-y-auto">
                {upcomingDeadlines.filter(d => d.type === 'staff').length === 0 ? (
                  <p className="text-sm text-green-600 text-center py-2">No clearances expiring within 30 days</p>
                ) : (
                  upcomingDeadlines.filter(d => d.type === 'staff').slice(0, 4).map(alert => (
                    <div key={alert.id} className={`flex items-start gap-2 p-2 rounded ${alert.urgency === 'urgent' ? 'bg-red-50' : 'bg-yellow-50'}`}>
                      {alert.urgency === 'urgent' ? (
                        <XCircle className="w-4 h-4 text-[#dc3545] flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-[#ffc107] flex-shrink-0 mt-0.5" />
                      )}
                      <span className="text-sm text-gray-700">
                        <strong>{alert.entityName}</strong> – {alert.item} ({alert.daysUntil} days)
                      </span>
                    </div>
                  ))
                )}
              </div>
              <div className="flex flex-col gap-2 mt-4">
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2 text-[#1a365d] bg-gray-50 hover:bg-gray-100 h-auto py-2 border-gray-300"
                  onClick={() => handleGenerateStaffComplianceReport()}
                >
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">Staff Compliance Report</span>
                </Button>
                <Button 
                  onClick={() => navigate('/staff-records')}
                  className="w-full bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-semibold"
                >
                  Open Staff Records <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Child Record Alerts - Real data from deadlines */}
          <Card className="bg-white shadow-md border-t-4 border-t-[#fd7e14]" data-testid="child-alerts-section">
            <CardHeader className="pb-2 bg-gradient-to-b from-orange-50 to-white">
              <CardTitle className="text-base font-bold text-[#1a365d] flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-[#fd7e14]" />
                Child Record Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-36 overflow-y-auto">
                {upcomingDeadlines.filter(d => d.type === 'child').length === 0 ? (
                  <p className="text-sm text-green-600 text-center py-2">All child records meet app requirements</p>
                ) : (
                  upcomingDeadlines.filter(d => d.type === 'child').slice(0, 4).map(alert => (
                    <div key={alert.id} className={`flex items-start gap-2 p-2 rounded ${alert.urgency === 'urgent' ? 'bg-red-50' : 'bg-yellow-50'}`}>
                      {alert.urgency === 'urgent' ? (
                        <XCircle className="w-4 h-4 text-[#dc3545] flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-[#ffc107] flex-shrink-0 mt-0.5" />
                      )}
                      <span className="text-sm text-gray-700">
                        <strong>{alert.entityName}</strong> – {alert.item} ({alert.daysUntil} days)
                      </span>
                    </div>
                  ))
                )}
              </div>
              <div className="flex flex-col gap-2 mt-4">
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2 text-[#1a365d] bg-gray-50 hover:bg-gray-100 h-auto py-2 border-gray-300"
                  onClick={() => handleGenerateChildSummaryReport()}
                >
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">Child Records Summary</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2 text-[#1a365d] bg-gray-50 hover:bg-gray-100 h-auto py-2 border-gray-300"
                  onClick={() => setShowEnrolledReport(true)}
                >
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">Enrolled Children</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2 text-[#1a365d] bg-gray-50 hover:bg-gray-100 h-auto py-2 border-gray-300"
                  onClick={() => handleGenerateEmergencyContactList()}
                >
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium">Emergency Contact List</span>
                </Button>
                <Button 
                  onClick={() => navigate('/child-records')}
                  className="w-full bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-semibold"
                >
                  View Child Records <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Third card placeholder - will be replaced */}
          <Card className="bg-white shadow-md border-t-4 border-t-[#28a745]" data-testid="facility-alerts-section">
            <CardHeader className="pb-2 bg-gradient-to-b from-green-50 to-white">
              <CardTitle className="text-base font-bold text-[#1a365d] flex items-center gap-2">
                <Building className="w-5 h-5 text-[#28a745]" />
                Facility Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-36 overflow-y-auto">
                {upcomingDeadlines.filter(d => d.type === 'facility').length === 0 ? (
                  <p className="text-sm text-green-600 text-center py-2">All facility items meet app requirements</p>
                ) : (
                  upcomingDeadlines.filter(d => d.type === 'facility').slice(0, 4).map(alert => (
                    <div key={alert.id} className={`flex items-start gap-2 p-2 rounded ${alert.urgency === 'urgent' ? 'bg-red-50' : 'bg-yellow-50'}`}>
                      {alert.urgency === 'urgent' ? (
                        <XCircle className="w-4 h-4 text-[#dc3545] flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-[#ffc107] flex-shrink-0 mt-0.5" />
                      )}
                      <span className="text-sm text-gray-700">
                        <strong>{alert.item}</strong> ({alert.daysUntil}d)
                      </span>
                    </div>
                  ))
                )}
              </div>
              <Button 
                onClick={() => {
                  // Scroll to compliance checklist
                  const element = document.querySelector('[data-testid="compliance-checklist"]');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full mt-4 bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-semibold"
                data-testid="go-to-facility-items-btn"
              >
                Go to Facility Items <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button 
                onClick={() => generateComplianceCategoryReport('facility_documents', 'Facility Compliance Report')}
                variant="outline"
                className="w-full mt-2 border-[#1a365d] text-[#1a365d] hover:bg-[#1a365d]/5 font-semibold"
                data-testid="print-facility-report-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Print Facility Report
              </Button>
            </CardContent>
          </Card>

          {/* Emergency Preparedness Alerts - derived from emergency compliance items */}
          <Card className="bg-white shadow-md border-t-4 border-t-[#e11d48]" data-testid="emergency-alerts-section">
            <CardHeader className="pb-2 bg-gradient-to-b from-rose-50 to-white">
              <CardTitle className="text-base font-bold text-[#1a365d] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#e11d48]" />
                Emergency Preparedness
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const emItems = (complianceItems || []).filter(i => i.category === 'emergency_preparedness' && i.isEnabled !== false);
                const emAlerts = emItems.filter(i => ['missing', 'expired', 'expiring'].includes(i.complianceStatus));
                return (
                  <>
                    <div className="space-y-2 max-h-36 overflow-y-auto">
                      {emAlerts.length === 0 ? (
                        <p className="text-sm text-green-600 text-center py-2">All emergency items meet app requirements</p>
                      ) : (
                        emAlerts.slice(0, 4).map(item => (
                          <div key={item.id} className={`flex items-start gap-2 p-2 rounded ${item.complianceStatus === 'expiring' ? 'bg-yellow-50' : 'bg-red-50'}`} data-testid={`emergency-alert-${item.id}`}>
                            {item.complianceStatus === 'expiring' ? (
                              <AlertTriangle className="w-4 h-4 text-[#ffc107] flex-shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="w-4 h-4 text-[#dc3545] flex-shrink-0 mt-0.5" />
                            )}
                            <span className="text-sm text-gray-700">
                              <strong>{item.name}</strong> — {item.complianceStatus === 'expiring' ? 'expiring' : item.complianceStatus === 'expired' ? 'expired' : 'missing'}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                    <Button 
                      onClick={() => {
                        const element = document.querySelector('[data-testid="compliance-checklist"]');
                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="w-full mt-4 bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-semibold"
                      data-testid="go-to-emergency-items-btn"
                    >
                      Go to Emergency Items <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                    <Button 
                      onClick={() => generateComplianceCategoryReport('emergency_preparedness', 'Emergency Preparedness Report')}
                      variant="outline"
                      className="w-full mt-2 border-[#1a365d] text-[#1a365d] hover:bg-[#1a365d]/5 font-semibold"
                      data-testid="print-emergency-report-btn"
                    >
                      <Printer className="w-4 h-4 mr-2" /> Print Emergency Report
                    </Button>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Center Messages & Notes - Stacked Vertically */}
        <div className="flex flex-col gap-4">
          {/* Notes Card */}
          <Card className="bg-white shadow-md" data-testid="notes-section">
            <CardHeader className="pb-2 border-b">
              <CardTitle className="text-base font-bold text-[#1a365d] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StickyNote className="w-5 h-5 text-[#6c757d]" />
                  Inspection Notes & Reminders
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={printNotesReport}
                  className="h-7 px-2 text-[#1a365d]"
                  data-testid="print-notes-report-btn"
                  title="Print Notes Report"
                >
                  <Printer className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddNote(!showAddNote)}
                  className="h-7 px-2 text-[#1a365d]"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                {/* Add Note Input */}
                {showAddNote && (
                  <div className="space-y-2 bg-blue-50 p-3 rounded-lg">
                    <Textarea
                      placeholder="Add a note or reminder..."
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      className="text-sm h-20 resize-none bg-white"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={addDirectorNote}
                        disabled={savingNote || !newNoteContent.trim()}
                        className="bg-[#1a365d] hover:bg-[#2d4a7c] text-white"
                      >
                        {savingNote ? "..." : "Add Note"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setShowAddNote(false); setNewNoteContent(''); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Director Notes List */}
                <div className="space-y-2 max-h-36 overflow-y-auto">
                  {directorNotes.length === 0 ? (
                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                      <p className="text-gray-500 text-sm">No notes yet. Click + to add one.</p>
                    </div>
                  ) : (
                    directorNotes.slice(0, 4).map(note => (
                      <div key={note.id} className={`flex items-start gap-2 p-2 rounded ${note.isPinned ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50 hover:bg-gray-100'}`}>
                        <StickyNote className={`w-4 h-4 flex-shrink-0 mt-0.5 ${note.isPinned ? 'text-purple-500' : 'text-gray-400'}`} />
                        <div className="flex-1 text-sm">
                          <p className="text-gray-700">{note.content}</p>
                          <p className="text-xs text-gray-400 mt-1">{new Date(note.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => togglePinNote(note)}
                                  className="p-1 hover:bg-gray-200 rounded"
                                  data-testid={`pin-note-${note.id}`}
                                >
                                  {note.isPinned ? <PinOff className="w-3 h-3 text-purple-500" /> : <Pin className="w-3 h-3 text-gray-400" />}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{note.isPinned ? "Unpin note" : "Pin note to top"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <button
                            onClick={() => deleteDirectorNote(note.id)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {/* Upload Document Section - Moved here from Center Messages */}
                <div className="border-t pt-3 mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={docUploadTitle}
                      onChange={(e) => setDocUploadTitle(e.target.value)}
                      placeholder="Name this file (e.g. Fire Marshal Report)"
                      className="flex-1 border rounded-lg px-3 py-1.5 text-sm bg-white"
                      data-testid="inspection-doc-title-input"
                    />
                    <input
                      ref={docFileInputRef}
                      type="file"
                      multiple
                      onChange={handleDocUpload}
                      disabled={uploadingDoc || !docUploadTitle.trim()}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png"
                      id="inspection-doc-upload-file"
                    />
                    <Button
                      size="sm"
                      disabled={uploadingDoc}
                      onClick={() => {
                        if (!docUploadTitle.trim()) { toast.error("Enter a name first"); return; }
                        docFileInputRef.current?.click();
                      }}
                      className="bg-[#1a365d] hover:bg-[#2d4a7c] text-white flex-shrink-0"
                      data-testid="inspection-doc-upload-btn"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      {uploadingDoc ? "..." : "Upload"}
                    </Button>
                  </div>
                  {directorDocs.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {directorDocs.slice(0, 3).map(doc => (
                        <div key={doc.id} className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                          <FileText className="w-3 h-3 text-gray-500" />
                          <span className="text-gray-700 truncate max-w-[100px]">{doc.title}</span>
                          <button
                            onClick={() => openDirectorDocument(doc, false)}
                            className="p-0.5 hover:bg-blue-100 rounded"
                            title="View"
                            data-testid={`view-doc-${doc.id}`}
                          >
                            <Eye className="w-3 h-3 text-blue-500" />
                          </button>
                          <button
                            onClick={() => openDirectorDocument(doc, true)}
                            className="p-0.5 hover:bg-blue-100 rounded"
                            title="Print"
                            data-testid={`print-doc-${doc.id}`}
                          >
                            <Printer className="w-3 h-3 text-blue-500" />
                          </button>
                          <button
                            onClick={() => deleteDocument(doc.id)}
                            className="p-0.5 hover:bg-red-100 rounded"
                          >
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enter Inspection Mode - Standalone Card */}
        <FeatureGate feature="inspection_mode">
          <Card className="bg-gradient-to-r from-emerald-600 to-green-700 shadow-xl border-0 overflow-hidden ring-2 ring-emerald-300/50">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4 text-white">
                  <div className="p-3 bg-white/20 rounded-full shadow-lg">
                    <ClipboardCheck className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold drop-shadow-sm">Ready for Inspection?</h3>
                    <p className="text-white/90 text-sm">View your center's real-time compliance status</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowInspectionMode(true)}
                  className="bg-white text-emerald-700 hover:bg-emerald-50 px-8 py-3 text-lg font-bold shadow-lg rounded-lg border-2 border-emerald-200"
                  data-testid="enter-inspection-mode-btn"
                >
                  Enter Inspection Mode <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </FeatureGate>
      </div>
      
      {/* Compliance Checklist Manager - Admin Section */}
      <div className="mt-6" data-testid="compliance-checklist">
        <Collapsible open={showChecklistManager} onOpenChange={(open) => { setShowChecklistManager(open); if (open) fetchComplianceItems(); }}>
          <Card className={`border-2 shadow-lg transition-all duration-200 ${
            showChecklistManager 
              ? 'bg-white border-emerald-400' 
              : 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-500 shadow-emerald-200/50'
          }`}>
            <CollapsibleTrigger asChild>
              <CardHeader className={`cursor-pointer transition-colors ${
                showChecklistManager 
                  ? 'hover:bg-emerald-50 bg-gradient-to-r from-emerald-500/10 to-transparent' 
                  : 'bg-gradient-to-r from-emerald-100 to-green-100 hover:from-emerald-200 hover:to-green-200'
              }`}>
                <div className="flex items-center justify-between">
                  <CardTitle className={`text-lg font-bold flex items-center gap-2 ${
                    showChecklistManager ? 'text-emerald-700' : 'text-emerald-800'
                  }`}>
                    <Settings className="w-6 h-6" />
                    Compliance Checklist Manager
                    {!showChecklistManager && (
                      <span className="text-sm font-normal text-emerald-600 ml-2">(Click to expand)</span>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-emerald-600 text-white text-sm px-3 py-1 shadow-sm">
                      {complianceItems.filter(i => i.isEnabled !== false && !['children_records','staff_records'].includes(i.category)).length} Active Items
                    </Badge>
                    <div className={`p-1.5 rounded-full transition-colors shadow-sm ${
                      showChecklistManager ? 'bg-emerald-600' : 'bg-emerald-500'
                    }`}>
                      {showChecklistManager ? (
                        <ChevronUp className="w-5 h-5 text-white" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-white" />
                      )}
                    </div>
                  </div>
                </div>
                <p className={`text-sm mt-1 font-medium ${
                  showChecklistManager ? 'text-gray-600' : 'text-emerald-700'
                }`}>
                  {showChecklistManager ? 'Click to collapse' : 'Click to expand'} • Add, edit, or disable compliance checklist items
                </p>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <ComplianceChecklistManager 
                  items={complianceItems.filter(i => !['children_records','staff_records'].includes(i.category))}
                  onUpdate={handleUpdate}
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
      
      {/* Enrolled Children Report Modal */}
      <EnrolledChildrenReport 
        open={showEnrolledReport} 
        onOpenChange={setShowEnrolledReport} 
      />

      {/* Scroll button now handled globally in AppShell */}

      </FeatureGate>
    </AppShell>
  );
};

export default ComplianceDashboardPage;
